import type { FieldCatalogEntry } from '~/modules/query/ai/types';

import { createDefaultField, type SchemaField, type SchemaFieldType } from '~/lib/ai/schema';

const JSON_SCHEMA_DIALECT = 'https://json-schema.org/draft/2020-12/schema';
const DEFAULT_TITLE = 'Digital Assistant Input Schema';

export interface JsonSchemaProperty {
  description: string;
  type: SchemaFieldType;
}

export interface JsonSchemaDocument {
  $schema: string;
  additionalProperties: false;
  properties: Record<string, JsonSchemaProperty>;
  required: string[];
  title: string;
  type: 'object';
}

export interface SchemaFieldsToJsonSchemaResult {
  /**
   * Property names that do not resolve to a catalog entry. They are excluded
   * from `schema` instead of being guessed at, so the caller can surface them.
   */
  invalidPropertyNames: string[];
  schema: JsonSchemaDocument;
}

/**
 * Converts the builder's SchemaField[] state into a canonical JSON Schema
 * document. `type` always comes from the catalog entry the admin selected, so
 * the emitted schema cannot diverge from a registered business field.
 */
export function schemaFieldsToJsonSchema(
  fields: SchemaField[],
  catalog: FieldCatalogEntry[],
  assistantName?: string,
): SchemaFieldsToJsonSchemaResult {
  const catalogByKey = new Map(catalog.map((entry) => [entry.key, entry]));
  const properties: Record<string, JsonSchemaProperty> = {};
  const required: string[] = [];
  const invalidPropertyNames: string[] = [];

  for (const field of fields) {
    if (!field.propertyName) {
      continue;
    }

    const catalogEntry = catalogByKey.get(field.propertyName);
    if (!catalogEntry) {
      invalidPropertyNames.push(field.propertyName);
      continue;
    }

    properties[field.propertyName] = {
      description: field.description.trim() ? field.description : catalogEntry.description,
      type: catalogEntry.type,
    };

    if (field.required) {
      required.push(field.propertyName);
    }
  }

  return {
    invalidPropertyNames,
    schema: {
      $schema: JSON_SCHEMA_DIALECT,
      title: assistantName?.trim() ? `${assistantName.trim()} Input Schema` : DEFAULT_TITLE,
      type: 'object',
      properties,
      required,
      additionalProperties: false,
    },
  };
}

interface CanonicalJsonSchemaShape {
  properties: Record<string, unknown>;
  required?: unknown;
  type: 'object';
}

/**
 * Recognises the shape this application treats as a canonical JSON Schema:
 * `type === "object"` with a plain-object `properties`. Deliberately does not
 * depend on an exact `$schema` string. Exported so callers can distinguish
 * "recognisable but partially unmappable" from "not recognisable at all".
 */
export function isCanonicalJsonSchema(value: unknown): value is CanonicalJsonSchemaShape {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  if (candidate.type !== 'object') {
    return false;
  }

  const properties = candidate.properties;
  return typeof properties === 'object' && properties !== null && !Array.isArray(properties);
}

export interface JsonSchemaToSchemaFieldsResult {
  fields: SchemaField[];
  unmappableKeys: string[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Converts a stored inputPayloadSchema back into SchemaField[]. The catalog is
 * the source of truth for `type`; a stored custom description is restored when
 * present, otherwise the catalog description. A property key with no catalog
 * match is reported, not guessed at. Anything that is not a canonical schema
 * yields an empty result.
 */
export function jsonSchemaToSchemaFields(
  schema: unknown,
  catalog: FieldCatalogEntry[],
): JsonSchemaToSchemaFieldsResult {
  if (!isCanonicalJsonSchema(schema)) {
    return { fields: [], unmappableKeys: [] };
  }

  const catalogByKey = new Map(catalog.map((entry) => [entry.key, entry]));
  const requiredKeys = Array.isArray(schema.required)
    ? schema.required.filter((key): key is string => typeof key === 'string')
    : [];
  const fields: SchemaField[] = [];
  const unmappableKeys: string[] = [];

  for (const key of Object.keys(schema.properties)) {
    const catalogEntry = catalogByKey.get(key);
    if (!catalogEntry) {
      unmappableKeys.push(key);
      continue;
    }

    const rawProperty = schema.properties[key];
    const storedDescription =
      isPlainObject(rawProperty) && typeof rawProperty.description === 'string'
        ? rawProperty.description.trim()
        : '';

    fields.push({
      ...createDefaultField(),
      propertyName: catalogEntry.key,
      type: catalogEntry.type,
      description: storedDescription || catalogEntry.description,
      required: requiredKeys.includes(catalogEntry.key),
    });
  }

  return { fields, unmappableKeys };
}

export interface BusinessFieldPreviewEntry {
  business_field: string;
  description: string;
  type: SchemaFieldType;
  use_in_prompt: string;
}

export interface BusinessFieldsPreview {
  $schema: string;
  business_fields: BusinessFieldPreviewEntry[];
  required: string[];
  title: string;
  type: 'object';
}

/**
 * The human-readable preview of a generated schema: each field's catalog label
 * beside the exact `{{key}}` placeholder to type in the prompt. Display-only;
 * the stored schema keeps its canonical `properties` shape.
 */
export function jsonSchemaToBusinessFieldsPreview(
  schema: JsonSchemaDocument,
  catalog: FieldCatalogEntry[],
): BusinessFieldsPreview {
  const labelByKey = new Map(catalog.map((entry) => [entry.key, entry.label]));

  return {
    $schema: schema.$schema,
    title: schema.title,
    type: schema.type,
    business_fields: Object.entries(schema.properties).map(([key, property]) => ({
      business_field: labelByKey.get(key) ?? key,
      use_in_prompt: `{{${key}}}`,
      type: property.type,
      description: property.description,
    })),
    required: schema.required,
  };
}
